import { LayoutFullscreen } from "@/components/_layouts/LayoutFullscreen/LayoutFullscreen";
import {
  apiOnCall,
  auth,
  isProductionEnvironment,
  prettyPrintFirebaseErrorMessage,
} from "@/firebase";
import { useRedirectWhenUserLoads } from "@/hooks/useRedirectIfUserOnPageLoad";
import {
  cloneFinancialDeck,
  financialDeckModel,
} from "@/models/financialDecks";
import { Route } from "@/paths";
import {
  Anchor,
  Button,
  Container,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { updateProfile } from "firebase/auth";
import { useCreateUserWithEmailAndPassword } from "react-firebase-hooks/auth";
import { Link, useLocation } from "react-router-dom";

export const SignupPage = () => {
  const location = useLocation();

  useRedirectWhenUserLoads(Route.DealTracker);

  const [createUserWithEmailAndPassword, , loading, error] =
    useCreateUserWithEmailAndPassword(auth, { sendEmailVerification: false });

  const form = useForm({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      accessCode: "",
      terms: false,
    },

    validate: {
      firstName: (val: string) =>
        val.length < 2
          ? "First name should include at least 2 characters"
          : null,
      lastName: (val: string) =>
        val.length < 2
          ? "Last name should include at least 2 characters"
          : null,
      email: (val: string) =>
        /^\S+@\S+$/.test(val) ? null : "Email format is invalid",
      password: (val: string) =>
        val.length <= 6
          ? "Password should include at least 6 characters"
          : null,
    },
  });

  const handleSubmit = async () => {
    const result = await createUserWithEmailAndPassword(
      form.values.email,
      form.values.password,
    );

    if (!result || !result.user) return;

    const displayName = `${form.values.lastName}, ${form.values.firstName}`;

    await updateProfile(result.user, {
      displayName,
    });

    apiOnCall("sendCustomVerificationEmail", {
      displayName: displayName || "",
    });

    if (isProductionEnvironment) {
      apiOnCall("notifyUserInitialized", {
        displayName: displayName || "",
      });
    }

    if (!location.state?.cloneModelId) return;

    const entity = financialDeckModel.createEntityInstance({
      id: location.state.cloneModelId,
    });

    await cloneFinancialDeck(entity, {
      name: "Homepage Model",
    });
  };

  return (
    <LayoutFullscreen>
      <Container miw={400} size={400} my={40} pb={40}>
        <Group gap={0}>
          <Text
            flex={1}
            component={Link}
            to={Route.Login}
            ta={"center"}
            p={10}
            style={{ borderBottom: "2px solid transparent" }}
          >
            Login
          </Text>
          <Text
            flex={1}
            component={Link}
            to={Route.Signup}
            ta={"center"}
            p={10}
            style={{
              borderBottom: "2px solid var(--mantine-color-blue-5)",
            }}
            fw={600}
          >
            Create Account
          </Text>
        </Group>
        <Paper radius={0} withBorder shadow="md" p={30}>
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              <TextInput
                label="First Name"
                placeholder="Your first name"
                value={form.values.firstName}
                onChange={(event) =>
                  form.setFieldValue("firstName", event.currentTarget.value)
                }
                size="md"
                error={form.errors.firstName}
              />
              <TextInput
                label="Last Name"
                placeholder="Your last name"
                value={form.values.lastName}
                onChange={(event) =>
                  form.setFieldValue("lastName", event.currentTarget.value)
                }
                size="md"
                error={form.errors.lastName}
              />
              <TextInput
                required
                label="Email"
                placeholder="hello@example.com"
                value={form.values.email}
                onChange={(event) =>
                  form.setFieldValue("email", event.currentTarget.value)
                }
                error={form.errors.email}
                size="md"
              />
              <PasswordInput
                required
                label="Password"
                placeholder="Your password"
                value={form.values.password}
                onChange={(event) =>
                  form.setFieldValue("password", event.currentTarget.value)
                }
                error={form.errors.password}
                size="md"
              />
              <Button type="submit" loading={!!loading} size="md">
                Get Started
              </Button>
              <Text c="gray.6" size="sm" ta="center">
                By creating an account I agree with <br /> the{" "}
                <Anchor component={Link} to={Route.TermOfService}>
                  Terms of Services
                </Anchor>{" "}
                and{" "}
                <Anchor component={Link} to={Route.PrivacyPolicy}>
                  Privacy Policy
                </Anchor>
              </Text>
              {error && (
                <Text c="red" ta="center" mt={10}>
                  {prettyPrintFirebaseErrorMessage(error.message)}
                </Text>
              )}
            </Stack>
          </form>
        </Paper>
      </Container>
    </LayoutFullscreen>
  );
};
